'use client'

import { useState } from 'react'
import { LuCheck, LuCircleAlert, LuClock, LuRefreshCw } from 'react-icons/lu'

import { Button } from '@/core/components/form/button/button'
import Spinner from '@/core/components/spinner'
import { cn } from '@/core/utils/common'
import PaymentCheckoutForm from '@/features/veluvit/components/wallet/payment-checkout-form'
import PaymentRefundForm from '@/features/veluvit/components/wallet/payment-refund-form'
import { usePaymentOrder } from '@/features/veluvit/hooks/use-payment-order'
import {
  isCheckoutInterrupted,
  PAYMENT_STATUS_LABELS,
  paymentMethodLabel,
} from '@/features/veluvit/lib/payment-status'

import type { PaymentOrder } from '@/features/veluvit/schemas/payment.schema'

export { PAYMENT_STATUS_LABELS } from '@/features/veluvit/lib/payment-status'

const DESCRIPTIONS: Record<PaymentOrder['status'], string> = {
  pending:
    '인증 또는 승인 결과를 확인하고 있어요. 기존 주문의 상태를 먼저 확인해 주세요.',
  awaiting_deposit:
    '아래 계좌로 입금해 주세요. 입금이 확인된 뒤 상품이 지급돼요.',
  paid: '결제가 완료됐어요.',
  partially_refunded: '결제 금액의 일부가 환불됐어요.',
  refunded: '결제 금액이 전액 환불됐어요.',
  failed: '결제가 완료되지 않았어요. 아래 안내와 주문 상태를 확인해 주세요.',
}

const REFUND_STATUS: Record<string, string> = {
  pending: '접수',
  requested: '접수',
  approved: '승인·처리 대기',
  processing: '처리 중',
  complete: '완료',
  completed: '완료',
  rejected: '반려',
  failed: '처리 실패',
  cancelled: '취소',
}

export default function PaymentOrderPanel({
  ownerId,
  orderId,
}: {
  ownerId: string
  orderId: string
}) {
  const {
    order,
    busy,
    error,
    walletError,
    polling,
    refresh,
    check,
    refreshWallet,
  } = usePaymentOrder(ownerId, orderId)
  const [checkout, setCheckout] = useState(false)
  const [refund, setRefund] = useState(false)
  const account = order?.payment.virtualAccount
  const interrupted = order ? isCheckoutInterrupted(order) : false
  const failed = order?.status === 'failed' || interrupted
  const waiting =
    order?.status === 'pending' || order?.status === 'awaiting_deposit'
  let StatusIcon = LuCheck
  if (waiting) StatusIcon = LuClock
  if (failed) StatusIcon = LuCircleAlert
  const needsCheck = !order || waiting || !!error

  return (
    <section aria-label="주문 상태" className="text-left">
      {!order && busy && (
        <div className="flex items-center gap-3 py-8 text-c14 text-description">
          <Spinner />
          주문을 확인하고 있어요
        </div>
      )}
      {order && (
        <>
          <header className="flex flex-col items-center pt-8 pb-7 text-center over10:pt-12 over10:pb-9">
            <span
              aria-hidden="true"
              className={cn(
                'mb-5 grid size-16 place-items-center rounded-full',
                failed
                  ? 'bg-error/[0.08] text-error'
                  : 'bg-foreground/[0.05] text-foreground'
              )}
            >
              <StatusIcon className="size-8 stroke-[1.5]" />
            </span>
            <h2
              className="text-c28 font-bold tracking-[-0.025em] text-foreground over10:text-c32"
              aria-live="polite"
            >
              {interrupted
                ? '결제가 중단됐어요'
                : PAYMENT_STATUS_LABELS[order.status]}
            </h2>
            <p className="mt-2 text-c14 leading-6 text-description over10:text-c15">
              {interrupted
                ? '아래에서 이 주문의 결제창을 다시 열 수 있어요.'
                : DESCRIPTIONS[order.status]}
            </p>
            {order.payment.resultMessage && failed && (
              <p className="mt-3 text-c14 leading-6 text-error">
                {order.payment.resultMessage}
              </p>
            )}
          </header>
          <dl className="grid gap-4 border-y border-border py-6 text-c14 over10:text-c15">
            <div className="flex justify-between gap-4">
              <dt className="text-description">상품</dt>
              <dd className="min-w-0 max-w-[72%] text-right font-medium text-foreground">
                {order.product.name}
              </dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-description">결제 금액</dt>
              <dd className="font-semibold tabular-nums text-foreground">
                {order.product.amount.toLocaleString('ko-KR')}원
              </dd>
            </div>
            {(order.payment.easyPayName ||
              order.payment.confirmedPaymentOptionId) && (
              <div className="flex justify-between gap-4">
                <dt className="text-description">결제수단</dt>
                <dd className="text-right font-medium text-foreground">
                  {paymentMethodLabel(order)}
                </dd>
              </div>
            )}
            {order.refundedAmount > 0 && (
              <div className="flex justify-between gap-4">
                <dt className="text-description">환불 금액</dt>
                <dd className="tabular-nums text-foreground">
                  {order.refundedAmount.toLocaleString('ko-KR')}원
                </dd>
              </div>
            )}
            <div className="flex items-start justify-between gap-5">
              <dt className="shrink-0 text-description">주문 번호</dt>
              <dd className="min-w-0 max-w-[72%] break-all text-right text-c12 leading-5 text-description over10:text-c13">
                {order.id}
              </dd>
            </div>
          </dl>
          {order.status === 'awaiting_deposit' && (
            <section className="mt-5" aria-label="입금 계좌">
              {account ? (
                <dl className="grid gap-3 text-c14 over10:text-c15">
                  <div>
                    <dt className="text-description">은행</dt>
                    <dd className="mt-1 font-medium text-foreground">
                      {account.bankName}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-description">입금 계좌</dt>
                    <dd className="mt-1 break-all text-c20 font-bold tabular-nums text-foreground">
                      {account.number}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-description">입금 기한</dt>
                    <dd className="mt-1 text-foreground">
                      {account.expiresAt ||
                        '결제창에서 안내한 기한을 확인해 주세요.'}
                    </dd>
                  </div>
                </dl>
              ) : (
                <p className="text-c14 leading-6 text-description">
                  입금 계좌를 확인하고 있어요. 상태 확인을 눌러 다시 확인해
                  주세요.
                </p>
              )}
            </section>
          )}
          {!!order.refunds?.length && (
            <section className="mt-6" aria-label="환불 요청 내역">
              <h3 className="text-c16 font-semibold text-foreground">
                환불 요청 내역
              </h3>
              <ul className="mt-2 divide-y divide-border text-c14">
                {order.refunds.map((item) => (
                  <li key={item.id} className="flex justify-between gap-4 py-3">
                    <span className="tabular-nums text-foreground">
                      {item.amount.toLocaleString('ko-KR')}원
                    </span>
                    <span className="text-description">
                      {REFUND_STATUS[item.status.toLowerCase()] ??
                        '처리 상태 확인 중'}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}
      {error && (
        <p role="alert" className="mt-4 text-c14 leading-6 text-error">
          {error}
        </p>
      )}
      {needsCheck && (
        <div className="mt-4 flex justify-center">
          <Button
            skin="flat"
            variant="ghost"
            size="sm"
            loading={busy}
            onClick={() => void (order ? check() : refresh())}
          >
            {!busy && <LuRefreshCw aria-hidden="true" className="size-4" />}
            {order ? '결제 상태 다시 확인' : '주문 다시 불러오기'}
          </Button>
        </div>
      )}
      {polling && (
        <p
          role="status"
          className="mt-2 text-center text-c12 leading-5 text-description over10:text-c13"
        >
          주문 상태를 자동으로 확인하고 있어요.
        </p>
      )}
      {walletError && (
        <div className="mt-4 space-y-2">
          <p role="alert" className="text-c14 leading-6 text-error">
            결제 상태는 확인했지만 지갑을 갱신하지 못했어요. {walletError}
          </p>
          <Button
            skin="flat"
            variant="secondary"
            onClick={() => void refreshWallet()}
          >
            지갑 다시 불러오기
          </Button>
        </div>
      )}
      {order?.status === 'pending' && order.provider === 'toss' && (
        <div className="mt-6 border-t border-border pt-5">
          <Button
            skin="flat"
            variant="secondary"
            onClick={() => setCheckout((value) => !value)}
          >
            {checkout ? '결제 정보 접기' : '이 주문으로 결제창 다시 열기'}
          </Button>
          {checkout && (
            <div className="mt-5">
              <PaymentCheckoutForm
                key={order.id}
                ownerId={ownerId}
                order={order}
              />
            </div>
          )}
        </div>
      )}
      {order &&
        (order.status === 'paid' || order.status === 'partially_refunded') && (
          <div className="mt-4">
            {!refund && (
              <Button variant="ghost" size="sm" onClick={() => setRefund(true)}>
                환불 요청
              </Button>
            )}
            {refund && (
              <div className="border-t border-border pt-5">
                <PaymentRefundForm
                  ownerId={ownerId}
                  order={order}
                  onRequested={refresh}
                  onCancel={() => setRefund(false)}
                />
              </div>
            )}
          </div>
        )}
    </section>
  )
}
